import cx from "classnames";

import { useTranslation } from "@/core/i18n";
import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import Memberships from "atoms/icon/icons/memberships_filled.svg";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";

export interface TreatmentProps {
  id: string;
  imgUrl?: string | null;
  type: string;
  bonus?: string;
  title: string;
  active?: boolean;
  remainingTreatmentsCount?: number;
  totalTreatments?: number;
  session?: string | null;
  onClick?: () => void;
  packageUnitType?: Models<"PackageUnitTypeModel"> | null | undefined;
}

export const Treatment = ({
  imgUrl,
  type,
  title,
  bonus,
  active,
  remainingTreatmentsCount,
  onClick,
  session = null,
  packageUnitType,
}: TreatmentProps) => {
  const { t } = useTranslation();
  const isPreviousTreatment = active === false;

  const typeTagTitle: Record<string, string> = {
    Treatment: t("treatment.typeTreatment"),
    Package: t("treatment.typePackage"),
    MembershipTreatment: t("treatment.typeMemberBenefit"),
    Membership: t("treatment.typeMembership"),
    Product: t("treatment.typeProduct"),
    ProductFromExternalSource: t("treatment.typeProduct"),
    GiftCard: t("treatment.typeGiftCard"),
  };

  return (
    <div
      className="flex cursor-pointer items-center justify-between bg-white py-2 pl-2 pr-4"
      onClick={onClick}
    >
      <div className="flex">
        {imgUrl && (
          <div className="mr-3 h-[86px] w-[86px] self-center">
            <img
              className={cx(
                "h-[86px] w-[86px] max-w-none rounded object-cover",
                isPreviousTreatment && "opacity-90",
              )}
              src={imgUrl}
              height={86}
              alt={t("treatment.typeTreatment")}
            />
          </div>
        )}
        <div className={cx("my-2 mr-2", !imgUrl && "pl-2")}>
          <div className={cx("flex gap-1 pb-2", isPreviousTreatment && "opacity-90")}>
            {type === "MembershipTreatment" && (
              <Icon color="brandColor" size="x2small" svg={Memberships} />
            )}
            <DescriptiveTag color="secondary" size="small">
              {bonus ?? typeTagTitle[type]}
            </DescriptiveTag>
          </div>

          <div
            className={cx(
              "flex flex-col items-start justify-start pb-0.5 pr-4 text-left font-sans text-bold1",
              isPreviousTreatment ? "text-secondary" : "text-primary",
            )}
          >
            {title}
          </div>
          {session ? (
            <div className="text-left font-sans text-body2 text-secondary">{session}</div>
          ) : (
            <div
              className={cx(
                "text-left font-sans text-body2",
                isPreviousTreatment ? "text-dark-grey" : "text-secondary",
              )}
            >
              {remainingTreatmentsCount}{" "}
              {type === "Package"
                ? t("treatment.nounTreatment", { count: remainingTreatmentsCount ?? 0 })
                : (((remainingTreatmentsCount ?? 0) === 1
                    ? packageUnitType?.singularUnitType
                    : packageUnitType?.pluralUnitType) ?? "")}{" "}
              {t("treatment.available")}
            </div>
          )}
        </div>
      </div>
      {!session && <Icon svg={ChevronRight} size="xsmall" color="brandColor" />}
    </div>
  );
};
